import React from 'react';
import { useTranslation } from 'react-i18next';
import ClientsCarousel from '../../components/Carousel/ClientsCarousel';
import Hero1 from "../../assets/hero1.png";
import Hero2 from "../../assets/hero2.png";
import logo from "../../assets/logo.png";
import Dashboard from "../../assets/dashboard.png";
import { LuArrowRight, LuCalculator, LuFileText, LuStar, LuUsers } from "react-icons/lu";
import YrosPages from '../../components/Info/YrosPages';
import Temps from "../../assets/temps.svg";
import Contrôle from "../../assets/controle.svg";
import Fiabilité from "../../assets/fiabilite.svg";
import ImagePaie from "../../assets/image_votre_paie_a_jour.svg";
import YrosProcess from '../../components/Info/YrosProcess';
import AdvantageCard from '../../components/Info/AdvantageCard';
import { motion } from "framer-motion"; 

function Home() {
  const { t } = useTranslation();
  const features = [
    {
      icon: <LuCalculator className="w-7 h-7" aria-hidden="true" />,
      title: t('home.features.calc_title'),
      description: t('home.features.calc_desc'),
      bgColor: "bg-gradient-to-br from-orange-50 to-orange-100",
      iconColor: "text-orange-600",
      accentColor: "border-orange-200"
    },
    {
      icon: <LuFileText className="w-7 h-7" aria-hidden="true" />,
      title: t('home.features.declarations_title'),
      description: t('home.features.declarations_desc'),
      bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      iconColor: "text-emerald-600",
      accentColor: "border-emerald-200"
    },
    {
      icon: <LuUsers className="w-7 h-7" aria-hidden="true" />,
      title: t('home.features.movements_title'),
      description: t('home.features.movements_desc'),
      bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "text-blue-600",
      accentColor: "border-blue-200"
    }
  ];

  const advantages = [
    {
      title: t('home.advantages.time_title'),
      description: <>{t('home.advantages.time_desc_line1')} <br />{t('home.advantages.time_desc_line2')}</>,
      image: Temps,
      alt: t('home.advantages.time_alt')
    },
    {
      title: t('home.advantages.control_title'),
      description: <>{t('home.advantages.control_desc')}</>,
      image: Contrôle,
      alt: t('home.advantages.control_alt')
    },
    {
      title: t('home.advantages.reliability_title'),
      description: <>{t('home.advantages.reliability_desc_line1')}<br />{t('home.advantages.reliability_desc_line2')}</>,
      image: Fiabilité,
      alt: t('home.advantages.reliability_alt')
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <main className="overflow-hidden">
      {/* Header Section with Enhanced Design */}
      <header className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50 pt-20 pb-16">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="text-center"
          >
            {/* Floating Icons */}
            <motion.img
              src={Hero1}
              alt={t('home.decorative_icon_alt')}
              className="w-12 h-12 absolute left-4 top-4 md:left-10 md:top-10"
              loading="lazy"
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            />
            
            <motion.img
              src={Hero2}
              alt={t('home.decorative_icon_alt')}
              className="w-16 h-16 absolute right-4 top-24 md:right-10 md:top-32"
              loading="lazy"
              animate={{ 
                y: [0, 15, 0],
                rotate: [0, -5, 5, 0]
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 1
              }}
            />

            {/* Badge */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full px-4 py-2 mb-8 shadow-lg"
            >
              <LuStar className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-gray-700">{t('home.badge')}</span>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6"
            >
              {t('home.hero_title_line1')}{" "}
              <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                {t('home.hero_title_highlight')}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto mb-12"
            >
              {t('home.hero_subtitle')}
            </motion.p>

            {/* Enhanced CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                aria-label={t('home.cta_demo_aria')}
                onClick={() => window.location.href = "/demo"}
              >
                {t('home.cta_demo')}
                <LuArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/90 backdrop-blur-sm border-2 border-orange-200 text-orange-600 font-semibold px-8 py-4 rounded-2xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300"
                aria-label={t('home.cta_contact_aria')}
                onClick={() => window.location.href = "/contact"}
              >
                {t('home.cta_contact')}
              </motion.button>
            </motion.div>

            {/* Enhanced Dashboard Preview */}
            <motion.div
              variants={itemVariants}
              className="relative max-w-6xl mx-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 rounded-[3rem] blur-2xl transform scale-105"></div>
              <img
                src={Dashboard}
                alt={t('home.dashboard_alt')}
                className="relative w-full border-8 border-white rounded-[3rem] shadow-2xl"
                loading="lazy"
              />
            </motion.div>
          </motion.div>
        </div>
      </header>

      <ClientsCarousel />

      {/* Enhanced Features Section */}
      <motion.section
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  {t('home.features_heading_line1')}{" "}
                  <span className="bg-gradient-to-r from-green-500 to-blue-500 bg-clip-text text-transparent">
                    {t('home.features_heading_highlight')}
                  </span>{" "}
                  {t('home.features_heading_line2')}
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {t('home.features_subtitle')}
                </p>
              </motion.div>
            </div>

            {/* Right Features */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  whileHover={{ 
                    scale: 1.03, 
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)" 
                  }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${feature.accentColor} group`}
                >
                  <div className="flex items-start gap-6">
                    <div className={`${feature.bgColor} ${feature.iconColor} p-4 rounded-2xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* YrosPages Section */}
      <motion.section
        className="bg-gray-50 py-16"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <YrosPages />
      </motion.section>

      {/* Enhanced Advantages Section */}
      <motion.section
        className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              {t('home.advantages_heading')}{" "}
              <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                {t('home.advantages_heading_highlight')}
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              {t('home.advantages_subtitle')}
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 px-4">
            {advantages.map((adv, idx) => (
              <motion.div
                key={adv.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
              >
                <AdvantageCard
                  {...adv}
                  style={
                    idx === 0
                      ? "transform md:-rotate-2 hover:rotate-0"
                      : idx === 1
                      ? "transform md:scale-105"
                      : "transform md:rotate-2 hover:rotate-0"
                  }
                />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Enhanced Call-to-Action Section */}
      <motion.section
        className="relative py-20 px-4 sm:px-8 mx-4 sm:mx-8 my-16 bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-100 rounded-[3rem] overflow-hidden"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-orange-300/30 to-yellow-300/30 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-yellow-300/30 to-orange-300/30 rounded-full blur-2xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {t('home.compliance_heading_line1')}{" "}
                <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  {t('home.compliance_heading_highlight')}
                </span>
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                {t('home.compliance_subtitle')}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                aria-label={t('home.compliance_cta_aria')}
              >
                {t('home.compliance_cta')}
                <LuArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-3xl blur-2xl transform scale-110"></div>
                <img
                  src={ImagePaie}
                  alt={t('home.compliance_image_alt')}
                  className="relative w-full max-w-md rounded-3xl shadow-2xl"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* YrosProcess Section */}
      <motion.section
        className="bg-white py-16"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <YrosProcess />
      </motion.section>

      {/* Enhanced Pricing Section */}
      <motion.section
        className="py-20 px-4 bg-gradient-to-b from-white to-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            >
              {t('home.pricing_heading')}{" "}
              <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                {t('home.pricing_heading_highlight')}
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600"
            >
              {t('home.pricing_subtitle')}
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
          </div>
        </div>
      </motion.section>

      {/* Enhanced Footer CTA */}
      <motion.section
        className="relative py-20 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {t('home.final_cta_heading')}{" "}
              <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                {t('home.final_cta_heading_highlight')}
              </span>
            </h2>

            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              {t('home.final_cta_subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold px-10 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
                onClick={() => window.location.href = "/contact"}
              >
                {t('home.final_cta_contact')}
                <LuArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white/30 text-white font-semibold px-10 py-4 rounded-2xl hover:bg-white/10 transition-all duration-300"
                onClick={() => window.location.href = "/demo"}
              >
                {t('home.final_cta_demo')}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </main>
  );
}

export default Home;
